const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

class AuthService {
  /**
   * Ottieni il token di autenticazione dal localStorage
   */
  getToken() {
    return localStorage.getItem('auth_token');
  }

  /**
   * Ottieni l'utente autenticato dal localStorage
   */
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Verifica se l'utente è autenticato
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Registrazione nuovo utente
   */
  async register(name, email, password, passwordConfirmation, inviteToken = null) {
    try {
      const body = {
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
      };

      if (inviteToken) {
        body.invite_token = inviteToken;
      }

      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Errore durante la registrazione');
      }

      // Supporto per risposta wrappata in "data"
      const payload = data.data || data;

      if (payload.token) {
        localStorage.setItem('auth_token', payload.token);
        const userToSave = payload.user || payload;
        localStorage.setItem('user', JSON.stringify(userToSave));
      }

      // Restituisce enrolled_course se lo studente è stato iscritto tramite invito
      return payload.enrolled_course || null;
    } catch (error) {
      console.error('Errore registrazione:', error);
      throw error;
    }
  }

  /**
   * Login utente
   */
  async login(email, password, inviteToken = null) {
    try {
      const body = {
        email,
        password,
      };

      if (inviteToken) {
        body.invite_token = inviteToken;
      }

      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Credenziali non valide');
      }

      // Supporto per risposta wrappata in "data"
      const payload = data.data || data;

      if (payload.token) {
        localStorage.setItem('auth_token', payload.token);
        // Salva solo l'oggetto utente se presente, altrimenti il payload
        const userToSave = payload.user || payload;
        localStorage.setItem('user', JSON.stringify(userToSave));
      }

      return data;
    } catch (error) {
      console.error('Errore login:', error);
      throw error;
    }
  }

  /**
   * Logout utente
   */
  async logout() {
    try {
      const token = this.getToken();

      if (token) {
        await fetch(`${API_BASE_URL}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Errore logout:', error);
    } finally {
      // Rimuovi sempre il token dal localStorage anche in caso di errore
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  }

  /**
   * Chiamata API autenticata generica
   */
  async authenticatedFetch(url, options = {}) {
    const token = this.getToken();

    if (!token) {
      throw new Error('Utente non autenticato');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Se il token non è valido (401), logout automatico
    if (response.status === 401) {
      this.logout();
      throw new Error('Sessione scaduta. Effettua nuovamente il login.');
    }

    return response;
  }

  /**
   * Ottieni il profilo utente dal server
   */
  async getProfile() {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/user`);
      if (!response.ok) {
        throw new Error('Impossibile recuperare il profilo');
      }
      const data = await response.json();
      const user = data.data || data; // Handle potential wrapping

      // Update local storage
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('Errore getProfile:', error);
      throw error;
    }
  }

  /**
   * Ottieni i corsi dell'utente autenticato
   */
  async getMyCourses() {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/my-courses`);

      if (!response.ok) {
        throw new Error('Errore durante il caricamento dei corsi');
      }

      const data = await response.json();
      return data.courses || [];
    } catch (error) {
      console.error('Errore caricamento corsi:', error);
      throw error;
    }
  }

  /**
   * Iscriviti a un corso
   */
  async enrollCourse(courseId) {
    try {
      const response = await this.authenticatedFetch(
        `${API_BASE_URL}/my-courses/${courseId}/enroll`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error('Errore durante l\'iscrizione al corso');
      }

      return await response.json();
    } catch (error) {
      console.error('Errore iscrizione corso:', error);
      throw error;
    }
  }

  /**
   * Disiscriviti da un corso
   */
  async unenrollCourse(courseId) {
    try {
      const response = await this.authenticatedFetch(
        `${API_BASE_URL}/my-courses/${courseId}/unenroll`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Errore durante la disiscrizione dal corso');
      }

      return await response.json();
    } catch (error) {
      console.error('Errore disiscrizione corso:', error);
      throw error;
    }
  }

  /**
   * Verifica se l'utente è iscritto a un corso
   */
  async checkEnrollment(courseId) {
    try {
      const response = await this.authenticatedFetch(
        `${API_BASE_URL}/my-courses/${courseId}/check-enrollment`
      );

      if (!response.ok) {
        throw new Error('Errore durante la verifica dell\'iscrizione');
      }

      const data = await response.json();
      return data.isEnrolled || false;
    } catch (error) {
      console.error('Errore verifica iscrizione:', error);
      throw error;
    }
  }
}

export default new AuthService();
